/**
 * Site-wide map of device/block id → highest ACTIVE alarm severity, for the
 * SLD mini cards' fault/warning badge (djin-cloud#684).
 *
 * Deliberately reuses `useSiteAlarms(siteId, { statuses: ['active'] })` — the
 * exact same argument shape `useDeviceConditions` passes — so the diagram and
 * the open detail panel share ONE React Query cache entry and one fetch, and
 * can never disagree about which rows exist. The narrowing to unacked rows
 * happens in `buildDeviceAlarmSeverityMap`, not in the query, for that reason:
 * a different `statuses` value would fork the cache key.
 */
import { useMemo } from 'react';
import { useSiteAlarms } from '@/hooks/useSiteAlarms';
import {
  buildDeviceAlarmSeverityMap,
  type DeviceAlarmSeverity,
} from '../utils/deviceAlarmSeverity';

const EMPTY_MAP: Map<string, DeviceAlarmSeverity> = new Map();

export function useDeviceAlarmSeverity(
  siteId: string | undefined,
): Map<string, DeviceAlarmSeverity> {
  const query = useSiteAlarms(siteId ?? '', { statuses: ['active'] });
  const rows = query.data?.alarms;

  return useMemo(() => {
    if (!siteId || !rows) return EMPTY_MAP;
    return buildDeviceAlarmSeverityMap(rows);
  }, [siteId, rows]);
}
